import maplibregl, { FullscreenControl, Map, NavigationControl, ScaleControl, type MapOptions } from "maplibre-gl";
import { DEFAULT_MAP_OPTIONS } from "../../config/map.config";
import { MapStyleService } from "./MapStyleService";
import type { BoundsTuple } from "../geojson/GeoJsonBounds";

export class MapService {
  private map: Map | null = null;
  private navigationControl: NavigationControl | null = null;

  initialize(element: HTMLElement, options: Partial<MapOptions> = {}): Map {
    this.map = new maplibregl.Map({
      container: element,
      ...DEFAULT_MAP_OPTIONS,
      ...options
    } as MapOptions);

    this.navigationControl = new NavigationControl({ showCompass: true, showZoom: true });
    this.map.addControl(this.navigationControl, "top-right");
    this.map.addControl(new FullscreenControl(), "top-right");
    this.map.addControl(new ScaleControl({ unit: "metric" }), "bottom-left");

    return this.map;
  }

  waitUntilReady(): Promise<Map> {
    const map = this.getMap();
    if (map.loaded()) {
      this.applyOriginalBasemapVisibility();
      return Promise.resolve(map);
    }
    return new Promise((resolve, reject) => {
      map.once("load", () => {
        this.applyOriginalBasemapVisibility();
        resolve(map);
      });
      map.once("error", (event) => reject(new Error(event?.error?.message ?? "Map style failed to load.")));
    });
  }

  applyOriginalBasemapVisibility(): void {
    MapStyleService.applyOriginalLabelFreeBasemap(this.getMap());
  }

  getMap(): Map {
    if (!this.map) throw new Error("Map has not been initialized.");
    return this.map;
  }

  fitBounds(bounds: BoundsTuple | null, padding = 40): void {
    if (!bounds) return;
    this.getMap().fitBounds(bounds, { padding, maxZoom: 6.5, duration: 650 });
  }

  setZoomControls(visible: boolean): void {
    const map = this.getMap();
    if (visible && !this.navigationControl) {
      this.navigationControl = new NavigationControl({ showCompass: true, showZoom: true });
      map.addControl(this.navigationControl, "top-right");
    }

    if (!visible && this.navigationControl) {
      map.removeControl(this.navigationControl);
      this.navigationControl = null;
    }
  }

  resize(): void {
    this.getMap().resize();
  }

  destroy(): void {
    this.map?.remove();
    this.map = null;
    this.navigationControl = null;
  }
}
